import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface ProjectAccessGrant {
  id: string
  project: Project | null
  granted_at: string | null
}

interface User {
  id: string
  email: string
  name: string | null
  role: 'admin' | 'collaborator' | 'viewer'
  status: 'invited' | 'active' | 'deactivated'
  invite_expires_at: string | null
  created_at: string
  path_access?: PathAccess[]
  app_access?: AppAccess[]
  storage_access?: StorageAccessGrant[]
  project_access?: ProjectAccessGrant[]
}

interface InviteResponse {
  user: User
  invite_url: string
  email_sent: boolean
}

interface Storage {
  id: string
  storage_id: string
  name: string
  description: string | null
  backend_type: 'gcs' | 'gdrive' | 'github' | 'm365' | 'globus'
  config?: Record<string, unknown>
  is_active: boolean
}

interface StorageAccessGrant {
  id: string
  storage: Storage
  granted_at: string | null
}

interface PathAccess {
  id: string
  storage_id: string
  storage?: Storage
  path_prefix: string
  permission: 'read' | 'write'
  granted_at: string
}

interface AppAccess {
  id: string
  application: {
    id: string
    app_id: string
    name: string
    description: string | null
    url: string
    is_active: boolean
  } | null
  granted_at: string | null
}

interface Application {
  id: string
  app_id: string
  name: string
  description: string | null
  url: string
  is_active: boolean
  mode?: string
  category?: string
  version?: string
  tags?: string[]
  display_order?: number
  ab_config?: { variants: Array<{ label: string; url: string; weight: number }> }
  overlay_config?: { show_feedback?: boolean; feedback_prompt?: string }
}

interface Role {
  name: string
  description: string
  can_manage_users: boolean
  can_manage_apps: boolean
  can_grant_access: boolean
  bypass_path_checks: boolean
  can_manage_storages: boolean
}

interface DeliverableStatusUpdate {
  id: string
  project_deliverable_id: string
  status: string
  note: string
  author_name: string
  author_email: string
  created_at: string
}

interface ProjectDeliverable {
  id: string
  project_id: string
  title: string
  description: string | null
  due_date: string | null
  completed_at: string | null
  verification: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  recent_updates?: DeliverableStatusUpdate[]
}

interface Project {
  id: string
  project_id: string
  name: string
  description: string | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
  storages?: { id: string; storage: Storage; path_filter: string | null }[]
  applications?: { id: string; application: Application }[]
  deliverables?: ProjectDeliverable[]
  documents?: ProjectDocument[]
}

interface ProjectDocument {
  id: string
  project_id: string
  filename: string
  storage_path: string
  content_type: string
  file_size: number
  document_type: 'contract' | 'sow' | 'amendment'
  sha256_hash: string | null
  processing_status: 'pending' | 'uploaded' | 'extracted' | 'complete' | 'failed'
  processing_error: string | null
  extracted_deliverables_count: number
  uploader_name: string
  uploader_email: string
  created_at: string | null
  processed_at: string | null
}

interface PersonIdentity {
  id: string
  provider: string
  identifier: string
  is_primary: boolean
}

interface Person {
  id: string
  name: string
  email: string
  title: string | null
  role: string | null
  manager_id: string | null
  user_id: string | null
  is_active: boolean
  created_at: string
  identities?: PersonIdentity[]
  manager?: { id: string; name: string } | null
  direct_reports?: Person[]
}

interface Goal {
  id: string
  title: string
  description: string | null
  owner_id: string
  assignee_id: string
  project_id: string | null
  status: 'not_started' | 'in_progress' | 'at_risk' | 'complete'
  due_date: string | null
  completed_at: string | null
  owner?: { id: string; name: string }
  assignee?: { id: string; name: string }
  project?: { id: string; name: string; project_id: string } | null
  deliverables?: Deliverable[]
}

interface Deliverable {
  id: string
  goal_id: string
  title: string
  assignee_id: string | null
  start_date: string | null
  due_date: string | null
  completed_at: string | null
  location_type: string | null
  location_path: string | null
  verification: string | null
  artifact_url: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  assignee?: { id: string; name: string } | null
  fulfills_project_deliverable_id?: string | null
  fulfills_project_deliverable?: {
    id: string
    title: string
    project_id: string
  } | null
}

export function Admin() {
  const [activeTab, setActiveTab] = useState<'users' | 'apps' | 'storages' | 'projects' | 'people'>('users')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<User[]>('/admin/users'),
  })

  const { data: selectedUser } = useQuery({
    queryKey: ['admin', 'users', selectedUserId],
    queryFn: () => api.get<User>(`/admin/users/${selectedUserId}`),
    enabled: !!selectedUserId,
  })

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<Role[]>('/admin/roles'),
  })

  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get<Application[]>('/applications'),
  })

  const { data: storages, isLoading: storagesLoading } = useQuery({
    queryKey: ['storages'],
    queryFn: () => api.get<Storage[]>('/storages?include_inactive=true&include_config=true'),
  })

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: () => api.get<Project[]>('/projects?include_inactive=true&include_resources=true'),
  })

  const { data: people, isLoading: peopleLoading } = useQuery({
    queryKey: ['admin', 'people'],
    queryFn: () => api.get<Person[]>('/people/admin/people?include_inactive=true&include_hierarchy=true'),
  })

  if (isLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading...</div>
  }

  return (
    <div className="max-w-6xl">
      <h1 className="text-3xl mb-8">Admin</h1>

      {/* Tab Navigation */}
      <div className="flex gap-4 mb-8 border-b border-[var(--color-border)]">
        <button
          onClick={() => setActiveTab('users')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('apps')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'apps'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Application Registry
        </button>
        <button
          onClick={() => setActiveTab('storages')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'storages'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Storage Registry
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'projects'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Projects
        </button>
        <button
          onClick={() => setActiveTab('people')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'people'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          People
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Users List */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="section-label">Users</h2>
            </div>
            <AddUserForm
              roles={roles}
              onCreated={() => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })}
            />
            <div className="space-y-2">
              {users?.map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className={`card-hover w-full text-left ${
                    selectedUserId === user.id ? 'border-[var(--color-brand)]' : ''
                  } ${user.status === 'deactivated' ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{user.email}</div>
                      {user.name && (
                        <div className="text-sm text-[var(--color-text-secondary)]">
                          {user.name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {user.status !== 'active' && (
                        <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeClass(user.status)}`}>
                          {statusLabel(user)}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded ${getRoleBadgeClass(user.role)}`}>
                        {user.role}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* User Details */}
          <section>
            {selectedUser ? (
              <UserDetails
                user={selectedUser}
                roles={roles}
                applications={applications}
                storages={storages}
                projects={projects}
                onUpdate={() => {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
                }}
              />
            ) : (
              <div className="text-[var(--color-text-secondary)]">
                Select a user to manage their access
              </div>
            )}
          </section>
        </div>
      )}
      {activeTab === 'apps' && (
        <ApplicationRegistry
          applications={applications}
          isLoading={appsLoading}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['applications'] })}
        />
      )}
      {activeTab === 'storages' && (
        <StorageRegistry
          storages={storages}
          isLoading={storagesLoading}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['storages'] })}
        />
      )}
      {activeTab === 'projects' && (
        <ProjectRegistry
          projects={projects}
          storages={storages}
          applications={applications}
          isLoading={projectsLoading}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] })}
        />
      )}
      {activeTab === 'people' && (
        <PeopleManagement
          people={people}
          users={users}
          projects={projects}
          isLoading={peopleLoading}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'people'] })}
        />
      )}
    </div>
  )
}

function getRoleBadgeClass(role: string) {
  switch (role) {
    case 'admin':
      return 'bg-red-900/50 text-red-300'
    case 'collaborator':
      return 'bg-blue-900/50 text-blue-300'
    case 'viewer':
      return 'bg-gray-800 text-gray-300'
    default:
      return 'bg-gray-800 text-gray-300'
  }
}

function inviteExpired(user: User) {
  return !!user.invite_expires_at && new Date(user.invite_expires_at) < new Date()
}

function statusLabel(user: User) {
  if (user.status === 'invited') {
    return inviteExpired(user) ? 'invited (expired)' : 'invited'
  }
  return user.status
}

function getStatusBadgeClass(status: User['status']) {
  switch (status) {
    case 'invited':
      return 'bg-yellow-900/50 text-yellow-300'
    case 'deactivated':
      return 'bg-gray-800 text-gray-400'
    default:
      return 'bg-gray-800 text-gray-300'
  }
}

function InviteLinkPanel({ result }: { result: InviteResponse }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(result.invite_url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-3 p-3 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] space-y-2">
      <p className="text-sm">
        {result.email_sent
          ? `Invitation emailed to ${result.user.email}. The link below also works if they miss it.`
          : 'Email is not configured — copy this link and send it to them yourself. It expires in 24 hours.'}
      </p>
      <div className="flex gap-2 items-center">
        <code className="text-xs flex-1 truncate">{result.invite_url}</code>
        <button onClick={copy} className="btn btn-secondary text-sm whitespace-nowrap">
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>
    </div>
  )
}

function AddUserForm({ roles, onCreated }: { roles?: Role[]; onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('viewer')
  const [result, setResult] = useState<InviteResponse | null>(null)

  const createUser = useMutation({
    mutationFn: () => api.post<InviteResponse>('/admin/users', { email, name, role }),
    onSuccess: (data) => {
      setResult(data)
      setEmail('')
      setName('')
      setRole('viewer')
      onCreated()
    },
  })

  if (!open) {
    return (
      <div className="mb-4">
        <button onClick={() => { setOpen(true); setResult(null) }} className="btn btn-secondary text-sm">
          + Add User
        </button>
        {result && <InviteLinkPanel result={result} />}
      </div>
    )
  }

  return (
    <div className="card mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Invite a new user</h3>
        <button
          onClick={() => setOpen(false)}
          className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        >
          Close
        </button>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          createUser.mutate()
        }}
        className="space-y-3"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input w-full"
          placeholder="email@example.com"
          required
        />
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input flex-1"
            placeholder="Name (optional)"
          />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="input">
            {(roles ?? []).map((r) => (
              <option key={r.name} value={r.name}>
                {r.name.charAt(0).toUpperCase() + r.name.slice(1)}
              </option>
            ))}
          </select>
        </div>
        {createUser.isError && (
          <p className="text-sm text-red-400">
            {(createUser.error as Error)?.message?.replace(/^\d+:\s*/, '') || 'Failed to create invite'}
          </p>
        )}
        <button type="submit" disabled={createUser.isPending} className="btn btn-primary">
          {createUser.isPending ? 'Creating...' : 'Send invite'}
        </button>
      </form>
      {result && <InviteLinkPanel result={result} />}
    </div>
  )
}

interface UserDetailsProps {
  user: User
  roles?: Role[]
  applications?: Application[]
  storages?: Storage[]
  projects?: Project[]
  onUpdate: () => void
}

function UserDetails({ user, roles, applications, storages, projects, onUpdate }: UserDetailsProps) {
  const [newPath, setNewPath] = useState('')
  const [newPathStorageId, setNewPathStorageId] = useState('')
  const [newPathPermission, setNewPathPermission] = useState<'read' | 'write'>('read')
  const [selectedAppId, setSelectedAppId] = useState('')
  const [selectedStorageId, setSelectedStorageId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [resendResult, setResendResult] = useState<InviteResponse | null>(null)
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  const invalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
    onUpdate()
  }

  const updateRole = useMutation({
    mutationFn: (role: string) => api.patch(`/admin/users/${user.id}`, { role }),
    onSuccess: invalidateUser,
  })

  const resendInvite = useMutation({
    mutationFn: () => api.post<InviteResponse>(`/admin/users/${user.id}/resend-invite`, {}),
    onSuccess: (data) => {
      setResendResult(data)
      invalidateUser()
    },
  })

  const deactivate = useMutation({
    mutationFn: () => api.post(`/admin/users/${user.id}/deactivate`, {}),
    onSuccess: invalidateUser,
  })

  const reactivate = useMutation({
    mutationFn: () => api.post(`/admin/users/${user.id}/reactivate`, {}),
    onSuccess: invalidateUser,
  })

  const grantPath = useMutation({
    mutationFn: (data: { storage_id: string; path_prefix: string; permission: string }) =>
      api.post(`/admin/users/${user.id}/paths`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['paths'] })  // Cascade to Dashboard
      setNewPath('')
    },
  })

  const revokePath = useMutation({
    mutationFn: (pathId: string) => api.delete(`/admin/users/${user.id}/paths/${pathId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['paths'] })  // Cascade to Dashboard
    },
  })

  const grantApp = useMutation({
    mutationFn: (applicationId: string) =>
      api.post(`/admin/users/${user.id}/apps`, { application_id: applicationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })  // Cascade to Dashboard/Apps pages
      setSelectedAppId('')
    },
  })

  const revokeApp = useMutation({
    mutationFn: (accessId: string) => api.delete(`/admin/users/${user.id}/apps/${accessId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })  // Cascade to Dashboard/Apps pages
    },
  })

  const grantStorage = useMutation({
    mutationFn: (storageId: string) =>
      api.post(`/admin/users/${user.id}/storages`, { storage_id: storageId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['storages'] })  // Cascade to Files page
      setSelectedStorageId('')
    },
  })

  const revokeStorage = useMutation({
    mutationFn: (accessId: string) => api.delete(`/admin/users/${user.id}/storages/${accessId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['storages'] })  // Cascade to Files page
    },
  })

  const grantProject = useMutation({
    mutationFn: (projectId: string) =>
      api.post(`/admin/users/${user.id}/projects`, { project_id: projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })  // Cascade to Dashboard/Projects
      queryClient.invalidateQueries({ queryKey: ['paths'] })  // Project access affects file access
      setSelectedProjectId('')
    },
  })

  const revokeProject = useMutation({
    mutationFn: (accessId: string) => api.delete(`/admin/users/${user.id}/projects/${accessId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })  // Cascade to Dashboard/Projects
      queryClient.invalidateQueries({ queryKey: ['paths'] })  // Project access affects file access
    },
  })

  // Filter out apps the user already has access to
  const availableApps = applications?.filter(
    (app) =>
      app.is_active &&
      !user.app_access?.some((access) => access.application?.id === app.id)
  )

  // Filter out storages the user already has access to
  const availableStorages = storages?.filter(
    (storage) =>
      storage.is_active &&
      !user.storage_access?.some((access) => access.storage?.id === storage.id)
  )

  // Get storages user has access to for path grants
  const accessibleStorages = storages?.filter(
    (storage) =>
      storage.is_active &&
      user.storage_access?.some((access) => access.storage?.id === storage.id)
  )

  // Filter out projects the user already has access to
  const availableProjects = projects?.filter(
    (project) =>
      project.is_active &&
      !user.project_access?.some((access) => access.project?.id === project.id)
  )

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-label">User Details</h2>
        <div className="card">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <div className="font-medium text-lg">{user.email}</div>
              {user.name && (
                <div className="text-[var(--color-text-secondary)]">{user.name}</div>
              )}
            </div>
            {user.status !== 'active' && (
              <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeClass(user.status)}`}>
                {statusLabel(user)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm text-[var(--color-text-secondary)]">Role:</label>
            <select
              value={user.role}
              onChange={(e) => updateRole.mutate(e.target.value)}
              className="input py-1 px-2"
            >
              {roles?.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 pt-4 border-t border-[var(--color-border)] flex flex-wrap gap-2">
            {user.status === 'invited' && (
              <button
                onClick={() => resendInvite.mutate()}
                disabled={resendInvite.isPending}
                className="btn btn-secondary text-sm"
              >
                {resendInvite.isPending ? 'Sending...' : 'Resend invite'}
              </button>
            )}
            {user.status !== 'deactivated' && user.id !== currentUser?.id && (
              <button
                onClick={() => {
                  if (window.confirm(`Deactivate ${user.email}? They will immediately lose portal access.`)) {
                    deactivate.mutate()
                  }
                }}
                disabled={deactivate.isPending}
                className="text-red-400 hover:text-red-300 text-sm px-3 py-1"
              >
                {deactivate.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            )}
            {user.status === 'deactivated' && (
              <button
                onClick={() => reactivate.mutate()}
                disabled={reactivate.isPending}
                className="btn btn-secondary text-sm"
              >
                {reactivate.isPending ? 'Reactivating...' : 'Reactivate'}
              </button>
            )}
          </div>
          {reactivate.isError && (
            <p className="text-sm text-red-400 mt-2">
              {(reactivate.error as Error)?.message?.replace(/^\d+:\s*/, '') || 'Failed to reactivate'}
            </p>
          )}
          {resendResult && <InviteLinkPanel result={resendResult} />}
        </div>
      </div>

      {/* Storage Access */}
      <div>
        <h3 className="section-label">Storage Access</h3>
        <div className="card space-y-3">
          {user.storage_access && user.storage_access.length > 0 ? (
            <div className="space-y-2">
              {user.storage_access.map((access) => (
                <div
                  key={access.id}
                  className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                >
                  <div>
                    <div className="font-medium">{access.storage?.name || 'Unknown Storage'}</div>
                    <div className="text-xs text-[var(--color-text-secondary)]">
                      {access.storage?.backend_type} - {access.storage?.storage_id}
                    </div>
                  </div>
                  <button
                    onClick={() => revokeStorage.mutate(access.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">No storage access granted</p>
          )}

          <div className="flex gap-2 pt-3 border-t border-[var(--color-border)]">
            <select
              value={selectedStorageId}
              onChange={(e) => setSelectedStorageId(e.target.value)}
              className="input flex-1"
            >
              <option value="">Select a storage...</option>
              {availableStorages?.map((storage) => (
                <option key={storage.id} value={storage.id}>
                  {storage.name} ({storage.backend_type})
                </option>
              ))}
            </select>
            <button
              onClick={() => selectedStorageId && grantStorage.mutate(selectedStorageId)}
              disabled={!selectedStorageId}
              className="btn btn-primary disabled:opacity-50"
            >
              Grant
            </button>
          </div>
          {availableStorages?.length === 0 && storages && storages.length > 0 && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              User has access to all available storages
            </p>
          )}
          {(!storages || storages.length === 0) && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              No storages registered. Register storages in the storage registry.
            </p>
          )}
        </div>
      </div>

      {/* Path Access */}
      <div>
        <h3 className="section-label">Path Access</h3>
        <div className="card space-y-3">
          {user.path_access && user.path_access.length > 0 ? (
            <div className="space-y-2">
              {user.path_access.map((path) => (
                <div
                  key={path.id}
                  className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                >
                  <div>
                    <code className="text-sm">{path.path_prefix}</code>
                    <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                      ({path.permission})
                    </span>
                    {path.storage && (
                      <span className="ml-2 text-xs bg-[var(--color-surface)] px-1.5 py-0.5 rounded">
                        {path.storage.name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => revokePath.mutate(path.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">No path access granted</p>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (newPath && newPathStorageId) {
                grantPath.mutate({ storage_id: newPathStorageId, path_prefix: newPath, permission: newPathPermission })
              }
            }}
            className="space-y-2 pt-3 border-t border-[var(--color-border)]"
          >
            <div className="flex gap-2">
              <select
                value={newPathStorageId}
                onChange={(e) => setNewPathStorageId(e.target.value)}
                className="input flex-1"
                required
              >
                <option value="">Select storage...</option>
                {accessibleStorages?.map((storage) => (
                  <option key={storage.id} value={storage.id}>
                    {storage.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPath}
                onChange={(e) => setNewPath(e.target.value)}
                placeholder="/clients/acme/"
                className="input flex-1"
              />
              <select
                value={newPathPermission}
                onChange={(e) => setNewPathPermission(e.target.value as 'read' | 'write')}
                className="input w-24"
              >
                <option value="read">Read</option>
                <option value="write">Write</option>
              </select>
              <button type="submit" disabled={!newPathStorageId || !newPath} className="btn btn-primary disabled:opacity-50">
                Grant
              </button>
            </div>
            {(!accessibleStorages || accessibleStorages.length === 0) && (
              <p className="text-xs text-[var(--color-text-secondary)]">
                Grant storage access first to enable path grants
              </p>
            )}
          </form>
        </div>
      </div>

      {/* App Access */}
      <div>
        <h3 className="section-label">App Access</h3>
        <div className="card space-y-3">
          {user.app_access && user.app_access.length > 0 ? (
            <div className="space-y-2">
              {user.app_access.map((access) => (
                <div
                  key={access.id}
                  className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                >
                  <div>
                    <div className="font-medium">
                      {access.application?.name || 'Unknown App'}
                    </div>
                    {access.application?.description && (
                      <div className="text-xs text-[var(--color-text-secondary)]">
                        {access.application.description}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => revokeApp.mutate(access.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">No app access granted</p>
          )}

          <div className="flex gap-2 pt-3 border-t border-[var(--color-border)]">
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              className="input flex-1"
            >
              <option value="">Select an application...</option>
              {availableApps?.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => selectedAppId && grantApp.mutate(selectedAppId)}
              disabled={!selectedAppId}
              className="btn btn-primary disabled:opacity-50"
            >
              Grant
            </button>
          </div>
          {availableApps?.length === 0 && applications && applications.length > 0 && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              User has access to all available applications
            </p>
          )}
          {(!applications || applications.length === 0) && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              No applications registered. Register apps in the application registry.
            </p>
          )}
        </div>
      </div>

      {/* Project Access */}
      <div>
        <h3 className="section-label">Project Access</h3>
        <div className="card space-y-3">
          {user.project_access && user.project_access.length > 0 ? (
            <div className="space-y-2">
              {user.project_access.map((access) => (
                <div
                  key={access.id}
                  className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                >
                  <div>
                    <div className="font-medium">
                      {access.project?.name || 'Unknown Project'}
                    </div>
                    {access.project?.description && (
                      <div className="text-xs text-[var(--color-text-secondary)]">
                        {access.project.description}
                      </div>
                    )}
                    {access.project && (
                      <div className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                        {access.project.start_date || '?'} - {access.project.end_date || '?'}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => revokeProject.mutate(access.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">No project access granted</p>
          )}

          <div className="flex gap-2 pt-3 border-t border-[var(--color-border)]">
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="input flex-1"
            >
              <option value="">Select a project...</option>
              {availableProjects?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => selectedProjectId && grantProject.mutate(selectedProjectId)}
              disabled={!selectedProjectId}
              className="btn btn-primary disabled:opacity-50"
            >
              Grant
            </button>
          </div>
          {availableProjects?.length === 0 && projects && projects.length > 0 && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              User has access to all available projects
            </p>
          )}
          {(!projects || projects.length === 0) && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              No projects created. Create projects in the Projects tab.
            </p>
          )}
          <p className="text-xs text-[var(--color-text-tertiary)] italic">
            Project access grants read access to all linked storages/paths within the project.
          </p>
        </div>
      </div>
    </div>
  )
}

interface ApplicationRegistryProps {
  applications?: Application[]
  isLoading: boolean
  onUpdate: () => void
}

interface ABVariant {
  label: string
  url: string
  weight: number
}

function ApplicationRegistry({ applications, isLoading, onUpdate }: ApplicationRegistryProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingApp, setEditingApp] = useState<Application | null>(null)
  const [formData, setFormData] = useState({
    app_id: '',
    name: '',
    url: '',
    description: '',
    mode: 'use' as string,
    category: '',
    version: '',
    tags: '' as string,
    show_feedback: true,
    feedback_prompt: '',
  })
  const [abVariants, setAbVariants] = useState<ABVariant[]>([
    { label: 'control', url: '', weight: 50 },
    { label: 'variant', url: '', weight: 50 },
  ])

  const createApp = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/applications', data),
    onSuccess: () => {
      onUpdate()
      setShowForm(false)
      resetForm()
    },
  })

  const updateApp = useMutation({
    mutationFn: ({ appId, data }: { appId: string; data: Record<string, unknown> }) =>
      api.patch(`/applications/${appId}`, data),
    onSuccess: () => {
      onUpdate()
      setEditingApp(null)
    },
  })

  const deleteApp = useMutation({
    mutationFn: (appId: string) => api.delete(`/applications/${appId}`),
    onSuccess: () => onUpdate(),
  })

  const resetForm = () => {
    setFormData({ app_id: '', name: '', url: '', description: '', mode: 'use', category: '', version: '', tags: '', show_feedback: true, feedback_prompt: '' })
    setAbVariants([{ label: 'control', url: '', weight: 50 }, { label: 'variant', url: '', weight: 50 }])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const tags = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : null
    const overlay_config = {
      show_feedback: formData.show_feedback,
      feedback_prompt: formData.feedback_prompt || undefined,
    }
    const ab_config = formData.mode === 'ab_test' ? { variants: abVariants } : null

    const payload: Record<string, unknown> = {
      app_id: formData.app_id,
      name: formData.name,
      url: formData.url,
      description: formData.description || null,
      mode: formData.mode,
      category: formData.category || null,
      version: formData.version || null,
      tags,
      overlay_config,
      ab_config,
    }

    if (editingApp) {
      await updateApp.mutateAsync({ appId: editingApp.app_id, data: payload })
    } else {
      await createApp.mutateAsync(payload)
    }
  }

  const startEdit = (app: Application) => {
    setEditingApp(app)
    const appAny = app as any
    setFormData({
      app_id: app.app_id,
      name: app.name,
      url: app.url,
      description: app.description || '',
      mode: appAny.mode || 'use',
      category: appAny.category || '',
      version: appAny.version || '',
      tags: (appAny.tags || []).join(', '),
      show_feedback: appAny.overlay_config?.show_feedback ?? true,
      feedback_prompt: appAny.overlay_config?.feedback_prompt || '',
    })
    if (appAny.ab_config?.variants) {
      setAbVariants(appAny.ab_config.variants)
    }
    setShowForm(true)
  }

  const cancelForm = () => {
    setShowForm(false)
    setEditingApp(null)
    resetForm()
  }

  if (isLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading applications...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="section-label">Registered Applications</h2>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm">
            Register App
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-medium">{editingApp ? 'Edit Application' : 'Register New Application'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                App ID (unique identifier)
              </label>
              <input
                type="text"
                value={formData.app_id}
                onChange={(e) => setFormData({ ...formData, app_id: e.target.value })}
                placeholder="my-app"
                className="input w-full"
                disabled={!!editingApp}
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Application"
                className="input w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Application URL
            </label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://myapp.example.com"
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the application"
              className="input w-full"
              rows={2}
            />
          </div>

          {/* Mode selector */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Mode
              </label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                className="input w-full"
              >
                <option value="use">Use</option>
                <option value="ab_test">A/B Testing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Category (optional)
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., visualization"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Version (optional)
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="e.g., 1.0.0"
                className="input w-full"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Tags (comma-separated, optional)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="geospatial, time-series, research"
              className="input w-full"
            />
          </div>

          {/* Overlay config */}
          <div className="border border-[var(--color-border)] rounded p-4 space-y-3">
            <h4 className="text-sm font-medium">Overlay Config</h4>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="show_feedback"
                checked={formData.show_feedback}
                onChange={(e) => setFormData({ ...formData, show_feedback: e.target.checked })}
                className="accent-[var(--color-brand)]"
              />
              <label htmlFor="show_feedback" className="text-sm">Show feedback pill</label>
            </div>
            {formData.show_feedback && (
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Feedback prompt text
                </label>
                <input
                  type="text"
                  value={formData.feedback_prompt}
                  onChange={(e) => setFormData({ ...formData, feedback_prompt: e.target.value })}
                  placeholder="How's it going?"
                  className="input w-full"
                />
              </div>
            )}
          </div>

          {/* A/B Testing config */}
          {formData.mode === 'ab_test' && (
            <div className="border border-[var(--color-border)] rounded p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">A/B Variants</h4>
                <button
                  type="button"
                  onClick={() => setAbVariants([...abVariants, { label: '', url: '', weight: 0 }])}
                  className="text-xs text-[var(--color-brand)] hover:underline"
                >
                  + Add Variant
                </button>
              </div>
              {abVariants.map((variant, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Label</label>
                    <input
                      type="text"
                      value={variant.label}
                      onChange={e => {
                        const next = [...abVariants]
                        next[i] = { ...next[i], label: e.target.value }
                        setAbVariants(next)
                      }}
                      className="input w-full"
                      placeholder="control"
                    />
                  </div>
                  <div className="col-span-6">
                    <label className="block text-xs text-[var(--color-text-secondary)] mb-1">URL</label>
                    <input
                      type="url"
                      value={variant.url}
                      onChange={e => {
                        const next = [...abVariants]
                        next[i] = { ...next[i], url: e.target.value }
                        setAbVariants(next)
                      }}
                      className="input w-full"
                      placeholder="https://..."
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Weight %</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={variant.weight}
                      onChange={e => {
                        const next = [...abVariants]
                        next[i] = { ...next[i], weight: parseInt(e.target.value) || 0 }
                        setAbVariants(next)
                      }}
                      className="input w-full"
                    />
                  </div>
                  <div className="col-span-1">
                    {abVariants.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setAbVariants(abVariants.filter((_, j) => j !== i))}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <p className="text-xs text-[var(--color-text-tertiary)]">
                Weights sum: {abVariants.reduce((s, v) => s + v.weight, 0)}% (should be 100)
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn btn-primary">
              {editingApp ? 'Save Changes' : 'Register'}
            </button>
            <button type="button" onClick={cancelForm} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {applications && applications.length > 0 ? (
        <div className="space-y-3">
          {applications.map((app) => {
            const appAny = app as any
            return (
              <div key={app.id} className="card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{app.name}</span>
                      <code className="text-xs bg-[var(--color-surface)] px-1.5 py-0.5 rounded">
                        {app.app_id}
                      </code>
                      {appAny.mode && appAny.mode !== 'use' && (
                        <span className="text-xs bg-[var(--color-surface)] px-2 py-0.5 rounded text-[var(--color-text-secondary)]">
                          {appAny.mode === 'ab_test' ? 'A/B Test' : appAny.mode}
                        </span>
                      )}
                      {!app.is_active && (
                        <span className="text-xs bg-yellow-900/50 text-yellow-300 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                      {app.url}
                    </div>
                    {app.description && (
                      <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {app.description}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(app)}
                      className="text-sm text-[var(--color-brand)] hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => updateApp.mutate({
                        appId: app.app_id,
                        data: { is_active: !app.is_active }
                      })}
                      className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    >
                      {app.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete ${app.name}? This will also remove all user access grants.`)) {
                          deleteApp.mutate(app.app_id)
                        }
                      }}
                      className="text-sm text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="card">
          <p className="text-[var(--color-text-secondary)]">
            No applications registered yet. Click "Register App" to add your first application.
          </p>
        </div>
      )}
    </div>
  )
}

interface StorageRegistryProps {
  storages?: Storage[]
  isLoading: boolean
  onUpdate: () => void
}

function StorageRegistry({ storages, isLoading, onUpdate }: StorageRegistryProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingStorage, setEditingStorage] = useState<Storage | null>(null)
  const [formData, setFormData] = useState<{
    storage_id: string
    name: string
    description: string
    backend_type: 'gcs' | 'gdrive' | 'github' | 'm365' | 'globus'
    config: Record<string, string | boolean>
  }>({
    storage_id: '',
    name: '',
    description: '',
    backend_type: 'gcs',
    config: {
      bucket: '',
      project_id: '',
    },
  })

  const createStorage = useMutation({
    mutationFn: (data: typeof formData) => api.post('/storages', data),
    onSuccess: () => {
      onUpdate()
      setShowForm(false)
      setFormData({ storage_id: '', name: '', description: '', backend_type: 'gcs', config: { bucket: '', project_id: '' } })
    },
  })

  const updateStorage = useMutation({
    mutationFn: ({ storageId, data }: { storageId: string; data: Partial<Storage> }) =>
      api.patch(`/storages/${storageId}`, data),
    onSuccess: () => {
      onUpdate()
      setEditingStorage(null)
      setShowForm(false)
    },
  })

  const deleteStorage = useMutation({
    mutationFn: (storageId: string) => api.delete(`/storages/${storageId}`),
    onSuccess: () => onUpdate(),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingStorage) {
      updateStorage.mutate({
        storageId: editingStorage.storage_id,
        data: {
          name: formData.name,
          description: formData.description || null,
          config: formData.config,
        },
      })
    } else {
      createStorage.mutate(formData)
    }
  }

  const startEdit = (storage: Storage) => {
    setEditingStorage(storage)
    const cfg = (storage.config || {}) as Record<string, string | boolean>
    setFormData({
      storage_id: storage.storage_id,
      name: storage.name,
      description: storage.description || '',
      backend_type: storage.backend_type,
      config: cfg,
    })
    setShowForm(true)
  }

  const cancelForm = () => {
    setShowForm(false)
    setEditingStorage(null)
    setFormData({ storage_id: '', name: '', description: '', backend_type: 'gcs', config: { bucket: '', project_id: '' } })
  }

  if (isLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading storages...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="section-label">Registered Storages</h2>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm">
            Register Storage
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-medium">{editingStorage ? 'Edit Storage' : 'Register New Storage'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Storage ID (unique identifier)
              </label>
              <input
                type="text"
                value={formData.storage_id}
                onChange={(e) => setFormData({ ...formData, storage_id: e.target.value })}
                placeholder="main-files"
                className="input w-full"
                disabled={!!editingStorage}
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Files"
                className="input w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Backend Type
            </label>
            <select
              value={formData.backend_type}
              onChange={(e) => setFormData({ ...formData, backend_type: e.target.value as typeof formData.backend_type })}
              className="input w-full md:w-64"
              disabled={!!editingStorage}
            >
              <option value="gcs">Google Cloud Storage</option>
              <option value="gdrive">Google Drive</option>
              <option value="github">GitHub Repository</option>
              <option value="m365" disabled>Microsoft 365 (coming soon)</option>
              <option value="globus" disabled>Globus (coming soon)</option>
            </select>
          </div>

          {formData.backend_type === 'gcs' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[var(--color-surface)] rounded-lg">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  GCS Bucket Name *
                </label>
                <input
                  type="text"
                  value={(formData.config.bucket as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, bucket: e.target.value } })}
                  placeholder="my-bucket-name"
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  GCP Project ID (optional)
                </label>
                <input
                  type="text"
                  value={(formData.config.project_id as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, project_id: e.target.value } })}
                  placeholder="my-gcp-project"
                  className="input w-full"
                />
              </div>
            </div>
          )}

          {formData.backend_type === 'gdrive' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[var(--color-surface)] rounded-lg">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Folder ID or Shared Drive ID *
                </label>
                <input
                  type="text"
                  value={(formData.config.folder_id as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, folder_id: e.target.value } })}
                  placeholder="1abc123..."
                  className="input w-full"
                  required
                />
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                  Find this in the folder URL after /folders/ or /drive/folders/
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={(formData.config.shared_drive as boolean) || false}
                    onChange={(e) => setFormData({ ...formData, config: { ...formData.config, shared_drive: e.target.checked } })}
                    className="rounded"
                  />
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    This is a Shared Drive (Team Drive)
                  </span>
                </label>
              </div>
            </div>
          )}

          {formData.backend_type === 'github' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[var(--color-surface)] rounded-lg">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Organization or Username *
                </label>
                <input
                  type="text"
                  value={(formData.config.owner as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, owner: e.target.value } })}
                  placeholder="my-org or username"
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Repository (optional)
                </label>
                <input
                  type="text"
                  value={(formData.config.repo as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, repo: e.target.value } })}
                  placeholder="Leave blank for all org repos"
                  className="input w-full"
                />
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                  Scope to a single repo, or leave blank to list all org repos
                </p>
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Token Secret Name *
                </label>
                <input
                  type="text"
                  value={(formData.config.token_secret as string) || ''}
                  onChange={(e) => setFormData({ ...formData, config: { ...formData.config, token_secret: e.target.value } })}
                  placeholder="github-pat-portal"
                  className="input w-full"
                  required
                />
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                  Secret in GCP Secret Manager containing a GitHub PAT
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the storage"
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn btn-primary">
              {editingStorage ? 'Save Changes' : 'Register'}
            </button>
            <button type="button" onClick={cancelForm} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {storages && storages.length > 0 ? (
        <div className="space-y-3">
          {storages.map((storage) => (
            <div key={storage.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{storage.name}</span>
                    <code className="text-xs bg-[var(--color-surface)] px-1.5 py-0.5 rounded">
                      {storage.storage_id}
                    </code>
                    <span className="text-xs bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded">
                      {storage.backend_type.toUpperCase()}
                    </span>
                    {!storage.is_active && (
                      <span className="text-xs bg-yellow-900/50 text-yellow-300 px-2 py-0.5 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  {storage.config && (
                    <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                      {storage.backend_type === 'gcs' && (
                        <>Bucket: {(storage.config as Record<string, string>).bucket}</>
                      )}
                      {storage.backend_type === 'gdrive' && (
                        <>Folder: {(storage.config as Record<string, string>).folder_id}{(storage.config as Record<string, boolean>).shared_drive ? ' (Shared Drive)' : ''}</>
                      )}
                      {storage.backend_type === 'github' && (
                        <>{(storage.config as Record<string, string>).owner}{(storage.config as Record<string, string>).repo ? `/${(storage.config as Record<string, string>).repo}` : ' (all repos)'} &middot; token: {(storage.config as Record<string, string>).token_secret || 'not set'}</>
                      )}
                    </div>
                  )}
                  {storage.description && (
                    <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                      {storage.description}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(storage)}
                    className="text-sm text-[var(--color-brand)] hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => updateStorage.mutate({
                      storageId: storage.storage_id,
                      data: { is_active: !storage.is_active }
                    })}
                    className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                  >
                    {storage.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete ${storage.name}? This will also remove all user access and path grants.`)) {
                        deleteStorage.mutate(storage.storage_id)
                      }
                    }}
                    className="text-sm text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <p className="text-[var(--color-text-secondary)]">
            No storages registered yet. Click "Register Storage" to add your first storage backend.
          </p>
        </div>
      )}
    </div>
  )
}

interface ProjectRegistryProps {
  projects?: Project[]
  storages?: Storage[]
  applications?: Application[]
  isLoading: boolean
  onUpdate: () => void
}

function ProjectRegistry({ projects, storages, applications, isLoading, onUpdate }: ProjectRegistryProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    start_date: '',
    end_date: '',
  })
  const [selectedStorageId, setSelectedStorageId] = useState('')
  const [selectedPathFilter, setSelectedPathFilter] = useState('')
  const [selectedAppId, setSelectedAppId] = useState('')
  const [expandedDeliverables, setExpandedDeliverables] = useState<Record<string, boolean>>({})
  const [showDeliverableForm, setShowDeliverableForm] = useState<string | null>(null)
  const [editingDeliverable, setEditingDeliverable] = useState<ProjectDeliverable | null>(null)
  const [deliverableForm, setDeliverableForm] = useState({
    title: '',
    description: '',
    due_date: '',
    verification: '',
  })
  const [showUpdateForm, setShowUpdateForm] = useState<string | null>(null) // deliverable id
  const [updateForm, setUpdateForm] = useState({ status: '', note: '' })

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null) // project id being uploaded to
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)

  const createProject = useMutation({
    mutationFn: (data: typeof formData) => api.post('/projects', data),
    onSuccess: () => {
      onUpdate()
      setShowForm(false)
      setFormData({ project_id: '', name: '', description: '', start_date: '', end_date: '' })
    },
  })

  const updateProject = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Partial<Project> }) =>
      api.patch(`/projects/${projectId}`, data),
    onSuccess: () => {
      onUpdate()
      setEditingProject(null)
      setShowForm(false)
    },
  })

  const deleteProject = useMutation({
    mutationFn: (projectId: string) => api.delete(`/projects/${projectId}`),
    onSuccess: () => onUpdate(),
  })

  const addStorage = useMutation({
    mutationFn: ({ projectId, storageId, pathFilter }: { projectId: string; storageId: string; pathFilter?: string }) =>
      api.post(`/projects/${projectId}/storages`, { storage_id: storageId, path_filter: pathFilter || null }),
    onSuccess: () => {
      onUpdate()
      setSelectedStorageId('')
      setSelectedPathFilter('')
    },
  })

  const removeStorage = useMutation({
    mutationFn: ({ projectId, psId }: { projectId: string; psId: string }) =>
      api.delete(`/projects/${projectId}/storages/${psId}`),
    onSuccess: () => onUpdate(),
  })

  const addApp = useMutation({
    mutationFn: ({ projectId, appId }: { projectId: string; appId: string }) =>
      api.post(`/projects/${projectId}/apps`, { application_id: appId }),
    onSuccess: () => {
      onUpdate()
      setSelectedAppId('')
    },
  })

  const removeApp = useMutation({
    mutationFn: ({ projectId, paId }: { projectId: string; paId: string }) =>
      api.delete(`/projects/${projectId}/apps/${paId}`),
    onSuccess: () => onUpdate(),
  })

  const createDeliverable = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: typeof deliverableForm }) =>
      api.post(`/projects/${projectId}/deliverables`, data),
    onSuccess: () => {
      onUpdate()
      setShowDeliverableForm(null)
      setDeliverableForm({ title: '', description: '', due_date: '', verification: '' })
    },
  })

  const updateDeliverable = useMutation({
    mutationFn: ({ projectId, deliverableId, data }: { projectId: string; deliverableId: string; data: Partial<ProjectDeliverable> }) =>
      api.patch(`/projects/${projectId}/deliverables/${deliverableId}`, data),
    onSuccess: () => {
      onUpdate()
      setEditingDeliverable(null)
      setShowDeliverableForm(null)
      setDeliverableForm({ title: '', description: '', due_date: '', verification: '' })
    },
  })

  const deleteDeliverable = useMutation({
    mutationFn: ({ projectId, deliverableId }: { projectId: string; deliverableId: string }) =>
      api.delete(`/projects/${projectId}/deliverables/${deliverableId}`),
    onSuccess: () => onUpdate(),
  })

  const createStatusUpdate = useMutation({
    mutationFn: ({ projectId, deliverableId, data }: {
      projectId: string
      deliverableId: string
      data: { status: string; note: string }
    }) => api.post(`/projects/${projectId}/deliverables/${deliverableId}/updates`, data),
    onSuccess: () => {
      onUpdate()
      setShowUpdateForm(null)
      setUpdateForm({ status: '', note: '' })
    },
  })

  // Document upload handlers
  const handleDocumentUpload = async (projectId: string, file: File) => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF and DOCX files are supported')
      return
    }

    setUploadingDoc(projectId)
    setUploadProgress('Getting upload URL...')

    try {
      // Step 1: Get signed upload URL
      const { uploadUrl, documentId } = await api.post<{
        uploadUrl: string
        documentId: string
        storagePath: string
      }>(`/projects/${projectId}/documents/upload-url`, {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
      })

      setUploadProgress('Uploading document...')

      // Step 2: Upload directly to GCS
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      // Step 3: Mark upload complete
      setUploadProgress('Upload complete!')
      await api.post(`/projects/${projectId}/documents/${documentId}/uploaded`, {})

      onUpdate()
      setTimeout(() => setUploadProgress(null), 2000)

    } catch (err) {
      console.error('Upload failed:', err)
      setUploadProgress('Upload failed. Please try again.')
      setTimeout(() => setUploadProgress(null), 3000)
    } finally {
      setUploadingDoc(null)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingProject) {
      updateProject.mutate({
        projectId: editingProject.project_id,
        data: {
          name: formData.name,
          description: formData.description || null,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
        },
      })
    } else {
      createProject.mutate(formData)
    }
  }

  const startEdit = (project: Project) => {
    setEditingProject(project)
    setFormData({
      project_id: project.project_id,
      name: project.name,
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
    })
    setShowForm(true)
  }

  const cancelForm = () => {
    setShowForm(false)
    setEditingProject(null)
    setFormData({ project_id: '', name: '', description: '', start_date: '', end_date: '' })
  }

  const handleDeliverableSubmit = (projectId: string) => {
    if (editingDeliverable) {
      updateDeliverable.mutate({
        projectId,
        deliverableId: editingDeliverable.id,
        data: {
          title: deliverableForm.title,
          description: deliverableForm.description || null,
          due_date: deliverableForm.due_date || null,
          verification: deliverableForm.verification || null,
        },
      })
    } else {
      createDeliverable.mutate({ projectId, data: deliverableForm })
    }
  }

  const startEditDeliverable = (projectId: string, deliverable: ProjectDeliverable) => {
    setEditingDeliverable(deliverable)
    setDeliverableForm({
      title: deliverable.title,
      description: deliverable.description || '',
      due_date: deliverable.due_date || '',
      verification: deliverable.verification || '',
    })
    setShowDeliverableForm(projectId)
  }

  const cancelDeliverableForm = () => {
    setShowDeliverableForm(null)
    setEditingDeliverable(null)
    setDeliverableForm({ title: '', description: '', due_date: '', verification: '' })
  }

  if (isLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading projects...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="section-label">Projects</h2>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm">
            Create Project
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-medium">{editingProject ? 'Edit Project' : 'Create New Project'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Project ID (unique identifier)
              </label>
              <input
                type="text"
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                placeholder="precision-ag-2026"
                className="input w-full"
                disabled={!!editingProject}
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Precision Agriculture 2026"
                className="input w-full"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the project"
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn btn-primary">
              {editingProject ? 'Save Changes' : 'Create'}
            </button>
            <button type="button" onClick={cancelForm} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {projects && projects.length > 0 ? (
        <div className="space-y-4">
          {projects.map((project) => {
            // Allow same storage with different paths, so don't filter out linked ones
            const linkedAppIds = project.applications?.map(pa => pa.application.id) || []
            const availableStorages = storages?.filter(s => s.is_active)
            const availableApps = applications?.filter(a => a.is_active && !linkedAppIds.includes(a.id))

            return (
              <div key={project.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{project.name}</span>
                      <code className="text-xs bg-[var(--color-surface)] px-1.5 py-0.5 rounded">
                        {project.project_id}
                      </code>
                      {!project.is_active && (
                        <span className="text-xs bg-yellow-900/50 text-yellow-300 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    {(project.start_date || project.end_date) && (
                      <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {project.start_date || '?'} - {project.end_date || '?'}
                      </div>
                    )}
                    {project.description && (
                      <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {project.description}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(project)}
                      className="text-sm text-[var(--color-brand)] hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => updateProject.mutate({
                        projectId: project.project_id,
                        data: { is_active: !project.is_active }
                      })}
                      className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    >
                      {project.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete ${project.name}?`)) {
                          deleteProject.mutate(project.project_id)
                        }
                      }}
                      className="text-sm text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Linked Resources */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[var(--color-border)]">
                  {/* Storages */}
                  <div>
                    <h4 className="text-sm text-[var(--color-text-secondary)] mb-2">Storages & Paths</h4>
                    {project.storages && project.storages.length > 0 ? (
                      <div className="space-y-1">
                        {project.storages.map((ps) => (
                          <div key={ps.id} className="flex items-center justify-between text-sm bg-[var(--color-surface)] px-2 py-1.5 rounded">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs px-1 py-0.5 bg-[var(--color-bg)] rounded">
                                  {ps.storage.backend_type}
                                </span>
                                <span className="font-medium truncate">{ps.storage.name}</span>
                              </div>
                              {ps.path_filter && (
                                <code className="text-xs text-[var(--color-text-secondary)] block mt-0.5 truncate">
                                  {ps.path_filter}
                                </code>
                              )}
                            </div>
                            <button
                              onClick={() => removeStorage.mutate({ projectId: project.project_id, psId: ps.id })}
                              className="text-red-400 hover:text-red-300 ml-2"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--color-text-secondary)]">No storages linked</p>
                    )}
                    {availableStorages && availableStorages.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <select
                          value={selectedStorageId}
                          onChange={(e) => setSelectedStorageId(e.target.value)}
                          className="input text-sm w-full py-1"
                        >
                          <option value="">Select storage...</option>
                          {availableStorages.map((s) => (
                            <option key={s.id} value={s.id}>{s.name} ({s.backend_type})</option>
                          ))}
                        </select>
                        {selectedStorageId && (
                          <input
                            type="text"
                            value={selectedPathFilter}
                            onChange={(e) => setSelectedPathFilter(e.target.value)}
                            placeholder={
                              availableStorages.find(s => s.id === selectedStorageId)?.backend_type === 'github'
                                ? 'repo-name or repo-name/path (optional)'
                                : '/folder/path (optional)'
                            }
                            className="input text-sm w-full py-1"
                          />
                        )}
                        <button
                          onClick={() => selectedStorageId && addStorage.mutate({
                            projectId: project.project_id,
                            storageId: selectedStorageId,
                            pathFilter: selectedPathFilter || undefined
                          })}
                          disabled={!selectedStorageId}
                          className="btn btn-secondary text-xs py-1 px-2 w-full disabled:opacity-50"
                        >
                          Add Storage
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Applications */}
                  <div>
                    <h4 className="text-sm text-[var(--color-text-secondary)] mb-2">Applications</h4>
                    {project.applications && project.applications.length > 0 ? (
                      <div className="space-y-1">
                        {project.applications.map((pa) => (
                          <div key={pa.id} className="flex items-center justify-between text-sm bg-[var(--color-surface)] px-2 py-1 rounded">
                            <span>{pa.application.name}</span>
                            <button
                              onClick={() => removeApp.mutate({ projectId: project.project_id, paId: pa.id })}
                              className="text-red-400 hover:text-red-300"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--color-text-secondary)]">No applications linked</p>
                    )}
                    {availableApps && availableApps.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        <select
                          value={selectedAppId}
                          onChange={(e) => setSelectedAppId(e.target.value)}
                          className="input text-sm flex-1 py-1"
                        >
                          <option value="">Add application...</option>
                          {availableApps.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => selectedAppId && addApp.mutate({ projectId: project.project_id, appId: selectedAppId })}
                          disabled={!selectedAppId}
                          className="btn btn-secondary text-xs py-1 px-2 disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Project Deliverables (Contractual) */}
                <div className="pt-4 border-t border-[var(--color-border)]">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setExpandedDeliverables(prev => ({ ...prev, [project.id]: !prev[project.id] }))}
                      className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    >
                      <span>{expandedDeliverables[project.id] ? '▼' : '▶'}</span>
                      <span>Contract Deliverables ({project.deliverables?.length || 0})</span>
                    </button>
                    {expandedDeliverables[project.id] && showDeliverableForm !== project.id && (
                      <button
                        onClick={() => setShowDeliverableForm(project.id)}
                        className="text-xs text-[var(--color-brand)] hover:underline"
                      >
                        + Add
                      </button>
                    )}
                  </div>

                  {expandedDeliverables[project.id] && (
                    <div className="space-y-2">
                      {showDeliverableForm === project.id && (
                        <div className="bg-[var(--color-surface)] p-3 rounded space-y-2">
                          <input
                            type="text"
                            value={deliverableForm.title}
                            onChange={(e) => setDeliverableForm({ ...deliverableForm, title: e.target.value })}
                            placeholder="Deliverable title"
                            className="input text-sm w-full py-1"
                          />
                          <textarea
                            value={deliverableForm.description}
                            onChange={(e) => setDeliverableForm({ ...deliverableForm, description: e.target.value })}
                            placeholder="Description (optional)"
                            className="input text-sm w-full py-1"
                            rows={2}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="date"
                              value={deliverableForm.due_date}
                              onChange={(e) => setDeliverableForm({ ...deliverableForm, due_date: e.target.value })}
                              className="input text-sm py-1"
                            />
                            <input
                              type="text"
                              value={deliverableForm.verification}
                              onChange={(e) => setDeliverableForm({ ...deliverableForm, verification: e.target.value })}
                              placeholder="How to verify"
                              className="input text-sm py-1"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDeliverableSubmit(project.project_id)}
                              disabled={!deliverableForm.title}
                              className="btn btn-primary text-xs py-1 disabled:opacity-50"
                            >
                              {editingDeliverable ? 'Save' : 'Add'}
                            </button>
                            <button
                              onClick={cancelDeliverableForm}
                              className="btn btn-secondary text-xs py-1"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {project.deliverables && project.deliverables.length > 0 ? (
                        project.deliverables.map((d) => (
                          <div key={d.id} className="bg-[var(--color-surface)] px-3 py-2 rounded">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{d.title}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    d.status === 'complete' ? 'bg-green-900/50 text-green-300' :
                                    d.status === 'in_progress' ? 'bg-blue-900/50 text-blue-300' :
                                    'bg-gray-800 text-gray-300'
                                  }`}>
                                    {d.status.replace('_', ' ')}
                                  </span>
                                </div>
                                {d.description && (
                                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{d.description}</p>
                                )}
                                <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)] mt-1">
                                  {d.due_date && <span>Due: {d.due_date}</span>}
                                  {d.verification && <span>Verify: {d.verification}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  value={d.status}
                                  onChange={(e) => updateDeliverable.mutate({
                                    projectId: project.project_id,
                                    deliverableId: d.id,
                                    data: { status: e.target.value as ProjectDeliverable['status'] }
                                  })}
                                  className="text-xs bg-transparent border border-[var(--color-border)] rounded px-1 py-0.5"
                                >
                                  <option value="not_started">Not Started</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="complete">Complete</option>
                                </select>
                                <button
                                  onClick={() => startEditDeliverable(project.id, d)}
                                  className="text-xs text-[var(--color-brand)] hover:underline"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm(`Delete deliverable "${d.title}"?`)) {
                                      deleteDeliverable.mutate({ projectId: project.project_id, deliverableId: d.id })
                                    }
                                  }}
                                  className="text-xs text-red-400 hover:text-red-300"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            {/* Status Updates Section */}
                            <div className="mt-2 pt-2 border-t border-[var(--color-border)]">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-[var(--color-text-secondary)]">Status Updates</span>
                                {showUpdateForm !== d.id && (
                                  <button
                                    onClick={() => {
                                      setShowUpdateForm(d.id)
                                      setUpdateForm({ status: d.status, note: '' })
                                    }}
                                    className="text-xs text-[var(--color-brand)] hover:underline"
                                  >
                                    + Add
                                  </button>
                                )}
                              </div>

                              {/* Add Update Form */}
                              {showUpdateForm === d.id && (
                                <div className="bg-[var(--color-bg)] p-2 rounded mb-2 space-y-2">
                                  <div className="flex gap-2">
                                    <select
                                      value={updateForm.status}
                                      onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                                      className="input text-xs py-1 flex-shrink-0"
                                    >
                                      <option value="not_started">Not Started</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="complete">Complete</option>
                                    </select>
                                  </div>
                                  <textarea
                                    value={updateForm.note}
                                    onChange={(e) => setUpdateForm({ ...updateForm, note: e.target.value })}
                                    placeholder="What's the status? Any blockers or progress notes..."
                                    className="input text-xs w-full py-1"
                                    rows={2}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => createStatusUpdate.mutate({
                                        projectId: project.project_id,
                                        deliverableId: d.id,
                                        data: updateForm
                                      })}
                                      disabled={!updateForm.note.trim()}
                                      className="btn btn-primary text-xs py-0.5 px-2 disabled:opacity-50"
                                    >
                                      Add Update
                                    </button>
                                    <button
                                      onClick={() => {
                                        setShowUpdateForm(null)
                                        setUpdateForm({ status: '', note: '' })
                                      }}
                                      className="btn btn-secondary text-xs py-0.5 px-2"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Recent Updates List */}
                              {d.recent_updates && d.recent_updates.length > 0 ? (
                                <div className="space-y-1">
                                  {d.recent_updates.map((u) => (
                                    <div key={u.id} className="text-xs bg-[var(--color-bg)] p-2 rounded">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-[var(--color-text-secondary)]">
                                          {new Date(u.created_at).toLocaleDateString()}
                                        </span>
                                        <span className="font-medium">{u.author_name}</span>
                                        <span className={`px-1 py-0.5 rounded ${
                                          u.status === 'complete' ? 'bg-green-900/50 text-green-300' :
                                          u.status === 'in_progress' ? 'bg-blue-900/50 text-blue-300' :
                                          'bg-gray-800 text-gray-300'
                                        }`}>
                                          {u.status.replace('_', ' ')}
                                        </span>
                                      </div>
                                      <p className="text-[var(--color-text-secondary)]">{u.note}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-[var(--color-text-secondary)] italic">No updates yet</p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        !showDeliverableForm && (
                          <p className="text-xs text-[var(--color-text-secondary)]">
                            No contract deliverables defined. These are the minimum bar promised to clients.
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* Contract Documents Section */}
                <div className="pt-4 border-t border-[var(--color-border)]">
                  <h4 className="text-sm text-[var(--color-text-secondary)] mb-2">
                    Contract Documents
                  </h4>

                  {/* Existing documents list */}
                  {project.documents && project.documents.length > 0 && (
                    <div className="space-y-3 mb-3">
                      {project.documents.map((doc) => (
                        <div key={doc.id} className="text-xs bg-[var(--color-surface)] p-3 rounded">
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-medium text-sm">{doc.filename}</span>
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                doc.processing_status === 'complete' ? 'bg-green-900/50 text-green-300' :
                                doc.processing_status === 'failed' ? 'bg-red-900/50 text-red-300' :
                                doc.processing_status === 'extracted' ? 'bg-blue-900/50 text-blue-300' :
                                'bg-gray-800 text-gray-300'
                              }`}>
                                {doc.processing_status}
                              </span>
                            </div>
                            <a
                              href="#"
                              onClick={async (e) => {
                                e.preventDefault()
                                const { url } = await api.get<{ url: string }>(`/projects/${project.project_id}/documents/${doc.id}/download`)
                                window.open(url, '_blank')
                              }}
                              className="text-[var(--color-brand)] hover:underline"
                            >
                              Download
                            </a>
                          </div>

                          {/* Storage location - TRANSPARENT */}
                          <div className="text-[var(--color-text-tertiary)] mb-2 font-mono text-xs break-all">
                            gs://rtgslab-portal-data-prod/{doc.storage_path}
                          </div>

                          {/* Metadata */}
                          <div className="text-[var(--color-text-secondary)] mb-2">
                            Uploaded {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''} by {doc.uploader_name}
                            {doc.extracted_deliverables_count > 0 && (
                              <span className="ml-2">• {doc.extracted_deliverables_count} deliverables extracted</span>
                            )}
                            {doc.sha256_hash && (
                              <span className="ml-2 font-mono">• SHA256: {doc.sha256_hash.slice(0, 12)}...</span>
                            )}
                          </div>

                          {/* Error message if failed */}
                          {doc.processing_error && (
                            <div className="text-red-400 mb-2">
                              Error: {doc.processing_error}
                            </div>
                          )}

                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload zone */}
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault()
                      const file = e.dataTransfer.files[0]
                      if (file) handleDocumentUpload(project.project_id, file)
                    }}
                    className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                      uploadingDoc === project.project_id
                        ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10'
                        : 'border-[var(--color-border)] hover:border-[var(--color-brand)]'
                    }`}
                  >
                    {uploadingDoc === project.project_id ? (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {uploadProgress}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          Drop contract/SOW here or{' '}
                          <button
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) handleDocumentUpload(project.project_id, file)
                              }
                              input.click()
                            }}
                            className="text-[var(--color-brand)] hover:underline"
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                          PDF or DOCX
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="card">
          <p className="text-[var(--color-text-secondary)]">
            No projects created yet. Click "Create Project" to add your first project.
          </p>
        </div>
      )}

    </div>
  )
}

interface PeopleManagementProps {
  people?: Person[]
  users?: User[]
  projects?: Project[]
  isLoading: boolean
  onUpdate: () => void
}

function PeopleManagement({ people, users, projects, isLoading, onUpdate }: PeopleManagementProps) {
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingPerson, setEditingPerson] = useState<Person | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    role: '',
    manager_id: '',
    user_id: '',
  })
  const queryClient = useQueryClient()

  const { data: selectedPerson } = useQuery({
    queryKey: ['admin', 'person', selectedPersonId],
    queryFn: () => api.get<Person>(`/people/admin/people/${selectedPersonId}`),
    enabled: !!selectedPersonId,
  })

  const createPerson = useMutation({
    mutationFn: (data: typeof formData) => api.post('/people/admin/people', {
      ...data,
      manager_id: data.manager_id || null,
      user_id: data.user_id || null,
    }),
    onSuccess: () => {
      onUpdate()
      setShowForm(false)
      setFormData({ name: '', email: '', title: '', role: '', manager_id: '', user_id: '' })
    },
  })

  const updatePerson = useMutation({
    mutationFn: ({ personId, data }: { personId: string; data: Partial<Person> }) =>
      api.patch(`/people/admin/people/${personId}`, data),
    onSuccess: () => {
      onUpdate()
      queryClient.invalidateQueries({ queryKey: ['admin', 'person', selectedPersonId] })
      setEditingPerson(null)
      setShowForm(false)
    },
  })

  const deletePerson = useMutation({
    mutationFn: (personId: string) => api.delete(`/people/admin/people/${personId}`),
    onSuccess: () => {
      onUpdate()
      setSelectedPersonId(null)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingPerson) {
      updatePerson.mutate({
        personId: editingPerson.id,
        data: {
          name: formData.name,
          email: formData.email,
          title: formData.title || null,
          role: formData.role || null,
          manager_id: formData.manager_id || null,
          user_id: formData.user_id || null,
        },
      })
    } else {
      createPerson.mutate(formData)
    }
  }

  const startEdit = (person: Person) => {
    setEditingPerson(person)
    setFormData({
      name: person.name,
      email: person.email,
      title: person.title || '',
      role: person.role || '',
      manager_id: person.manager_id || '',
      user_id: person.user_id || '',
    })
    setShowForm(true)
  }

  const cancelForm = () => {
    setShowForm(false)
    setEditingPerson(null)
    setFormData({ name: '', email: '', title: '', role: '', manager_id: '', user_id: '' })
  }

  // Build tree view of people with management hierarchy
  const buildPeopleTree = (peopleList: Person[]): Person[] => {
    const rootPeople = peopleList.filter(p => !p.manager_id)
    return rootPeople
  }

  const renderPersonItem = (person: Person, level: number = 0) => {
    const reports = people?.filter(p => p.manager_id === person.id) || []
    return (
      <div key={person.id}>
        <button
          onClick={() => setSelectedPersonId(person.id)}
          className={`card-hover w-full text-left ${
            selectedPersonId === person.id ? 'border-[var(--color-brand)]' : ''
          }`}
          style={{ marginLeft: `${level * 1}rem` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{person.name}</div>
              <div className="text-sm text-[var(--color-text-secondary)]">
                {person.email}
                {person.title && <span> - {person.title}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {person.user_id && (
                <span className="text-xs px-1.5 py-0.5 bg-green-900/50 text-green-300 rounded">
                  Linked
                </span>
              )}
              {!person.is_active && (
                <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">
                  Inactive
                </span>
              )}
            </div>
          </div>
        </button>
        {reports.length > 0 && (
          <div className="ml-4 border-l border-[var(--color-border)] pl-2 mt-1 space-y-1">
            {reports.map(report => renderPersonItem(report, level + 1))}
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading people...</div>
  }

  const rootPeople = people ? buildPeopleTree(people) : []

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* People List */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-label mb-0">People</h2>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm">
              Add Person
            </button>
          )}
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="card space-y-4 mb-4">
            <h3 className="font-medium">{editingPerson ? 'Edit Person' : 'Add New Person'}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="input w-full"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Job title"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Engineer, PhD Student"
                  className="input w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Manager
                </label>
                <select
                  value={formData.manager_id}
                  onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">No manager</option>
                  {people?.filter(p => p.id !== editingPerson?.id).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Link to User Account
                </label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">No linked account</option>
                  {users?.map((u) => (
                    <option key={u.id} value={u.id}>{u.email}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="submit" className="btn btn-primary">
                {editingPerson ? 'Save Changes' : 'Add Person'}
              </button>
              <button type="button" onClick={cancelForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="space-y-2">
          {rootPeople.length > 0 ? (
            rootPeople.map(person => renderPersonItem(person))
          ) : (
            <div className="card">
              <p className="text-[var(--color-text-secondary)]">
                No people added yet. Click "Add Person" to start.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Person Details */}
      <section>
        {selectedPerson ? (
          <PersonDetails
            person={selectedPerson}
            people={people}
            projects={projects}
            onEdit={() => startEdit(selectedPerson)}
            onDelete={() => {
              if (confirm(`Delete ${selectedPerson.name}?`)) {
                deletePerson.mutate(selectedPerson.id)
              }
            }}
            onUpdate={() => {
              onUpdate()
              queryClient.invalidateQueries({ queryKey: ['admin', 'person', selectedPersonId] })
            }}
          />
        ) : (
          <div className="text-[var(--color-text-secondary)]">
            Select a person to view their details and goals
          </div>
        )}
      </section>
    </div>
  )
}

interface PersonDetailsProps {
  person: Person
  people?: Person[]
  projects?: Project[]
  onEdit: () => void
  onDelete: () => void
  onUpdate: () => void
}

function PersonDetails({ person, people, projects, onEdit, onDelete, onUpdate }: PersonDetailsProps) {
  const [showIdentityForm, setShowIdentityForm] = useState(false)
  const [showGoalForm, setShowGoalForm] = useState(false)
  const [identityForm, setIdentityForm] = useState({ provider: 'github', identifier: '', is_primary: false })
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    owner_id: '',
    project_id: '',
    due_date: '',
  })
  const queryClient = useQueryClient()

  const { data: goals } = useQuery({
    queryKey: ['admin', 'person', person.id, 'goals'],
    queryFn: () => api.get<Goal[]>(`/people/admin/people/${person.id}/goals?include_deliverables=true`),
  })

  const addIdentity = useMutation({
    mutationFn: (data: typeof identityForm) =>
      api.post(`/people/admin/people/${person.id}/identities`, data),
    onSuccess: () => {
      onUpdate()
      setShowIdentityForm(false)
      setIdentityForm({ provider: 'github', identifier: '', is_primary: false })
    },
  })

  const removeIdentity = useMutation({
    mutationFn: (identityId: string) =>
      api.delete(`/people/admin/people/${person.id}/identities/${identityId}`),
    onSuccess: () => onUpdate(),
  })

  const createGoal = useMutation({
    mutationFn: (data: typeof goalForm) =>
      api.post(`/people/admin/people/${person.id}/goals`, {
        ...data,
        owner_id: data.owner_id || undefined,
        project_id: data.project_id || undefined,
        due_date: data.due_date || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'person', person.id, 'goals'] })
      setShowGoalForm(false)
      setGoalForm({ title: '', description: '', owner_id: '', project_id: '', due_date: '' })
    },
  })

  const deleteGoal = useMutation({
    mutationFn: (goalId: string) => api.delete(`/people/admin/goals/${goalId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'person', person.id, 'goals'] })
    },
  })

  return (
    <div className="space-y-6">
      {/* Person Info */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="section-label mb-0">Person Details</h2>
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-sm text-[var(--color-brand)] hover:underline">
              Edit
            </button>
            <button onClick={onDelete} className="text-sm text-red-400 hover:text-red-300">
              Delete
            </button>
          </div>
        </div>
        <div className="card">
          <div className="font-medium text-lg">{person.name}</div>
          <div className="text-[var(--color-text-secondary)]">{person.email}</div>
          {person.title && <div className="text-sm mt-1">{person.title}</div>}
          {person.role && (
            <div className="text-xs mt-1 inline-block px-2 py-0.5 bg-[var(--color-surface)] rounded">
              {person.role}
            </div>
          )}
          {person.manager && (
            <div className="text-sm text-[var(--color-text-secondary)] mt-2">
              Reports to: {person.manager.name}
            </div>
          )}
        </div>
      </div>

      {/* Identities */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="section-label mb-0">External Identities</h3>
          {!showIdentityForm && (
            <button
              onClick={() => setShowIdentityForm(true)}
              className="text-sm text-[var(--color-brand)] hover:underline"
            >
              + Add Identity
            </button>
          )}
        </div>
        <div className="card space-y-3">
          {person.identities && person.identities.length > 0 ? (
            <div className="space-y-2">
              {person.identities.map((identity) => (
                <div
                  key={identity.id}
                  className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 bg-[var(--color-surface)] rounded uppercase">
                      {identity.provider}
                    </span>
                    <code className="text-sm">{identity.identifier}</code>
                    {identity.is_primary && (
                      <span className="text-xs text-green-400">Primary</span>
                    )}
                  </div>
                  <button
                    onClick={() => removeIdentity.mutate(identity.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              No external identities linked
            </p>
          )}

          {showIdentityForm && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                addIdentity.mutate(identityForm)
              }}
              className="pt-3 border-t border-[var(--color-border)] space-y-2"
            >
              <div className="flex gap-2">
                <select
                  value={identityForm.provider}
                  onChange={(e) => setIdentityForm({ ...identityForm, provider: e.target.value })}
                  className="input"
                >
                  <option value="github">GitHub</option>
                  <option value="email">Email</option>
                  <option value="gcs">Google Cloud Storage</option>
                  <option value="gdrive">Google Drive</option>
                </select>
                <input
                  type="text"
                  value={identityForm.identifier}
                  onChange={(e) => setIdentityForm({ ...identityForm, identifier: e.target.value })}
                  placeholder="Username or email"
                  className="input flex-1"
                  required
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={identityForm.is_primary}
                    onChange={(e) => setIdentityForm({ ...identityForm, is_primary: e.target.checked })}
                  />
                  Primary identity for this provider
                </label>
                <div className="flex gap-2 ml-auto">
                  <button type="submit" className="btn btn-primary text-sm">Add</button>
                  <button
                    type="button"
                    onClick={() => setShowIdentityForm(false)}
                    className="btn btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Goals */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="section-label mb-0">Goals</h3>
          {!showGoalForm && (
            <button
              onClick={() => setShowGoalForm(true)}
              className="text-sm text-[var(--color-brand)] hover:underline"
            >
              + Add Goal
            </button>
          )}
        </div>

        {showGoalForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              createGoal.mutate(goalForm)
            }}
            className="card space-y-4 mb-4"
          >
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Goal Title *
              </label>
              <input
                type="text"
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                placeholder="What needs to be accomplished?"
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Description
              </label>
              <textarea
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder="Additional details..."
                className="input w-full"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Owner (Accountable)
                </label>
                <select
                  value={goalForm.owner_id}
                  onChange={(e) => setGoalForm({ ...goalForm, owner_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">{person.manager ? person.manager.name : person.name} (default)</option>
                  {people?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Project (Contract)
                </label>
                <select
                  value={goalForm.project_id}
                  onChange={(e) => setGoalForm({ ...goalForm, project_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">No project</option>
                  {projects?.filter(p => p.is_active).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={goalForm.due_date}
                  onChange={(e) => setGoalForm({ ...goalForm, due_date: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary">Create Goal</button>
              <button
                type="button"
                onClick={() => setShowGoalForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {goals && goals.length > 0 ? (
            goals.map((goal) => {
              // Find project deliverables if goal is linked to a project
              const projectDeliverables = goal.project_id
                ? projects?.find(p => p.project_id === goal.project?.project_id)?.deliverables
                : undefined
              return (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  people={people}
                  projectDeliverables={projectDeliverables}
                  onDelete={() => {
                    if (confirm(`Delete goal "${goal.title}"?`)) {
                      deleteGoal.mutate(goal.id)
                    }
                  }}
                  onUpdate={() => queryClient.invalidateQueries({
                    queryKey: ['admin', 'person', person.id, 'goals']
                  })}
                />
              )
            })
          ) : (
            <div className="card">
              <p className="text-[var(--color-text-secondary)]">
                No goals assigned yet
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface GoalCardProps {
  goal: Goal
  people?: Person[]
  projectDeliverables?: ProjectDeliverable[]
  onDelete: () => void
  onUpdate: () => void
}

function GoalCard({ goal, people, projectDeliverables, onDelete, onUpdate }: GoalCardProps) {
  const [showDeliverableForm, setShowDeliverableForm] = useState(false)
  const [deliverableForm, setDeliverableForm] = useState({
    title: '',
    assignee_id: '',
    due_date: '',
    location_type: '',
    location_path: '',
    verification: '',
    fulfills_project_deliverable_id: '',
  })

  const createDeliverable = useMutation({
    mutationFn: (data: typeof deliverableForm) =>
      api.post(`/people/admin/goals/${goal.id}/deliverables`, {
        ...data,
        assignee_id: data.assignee_id || undefined,
        due_date: data.due_date || undefined,
        location_type: data.location_type || undefined,
        location_path: data.location_path || undefined,
        verification: data.verification || undefined,
        fulfills_project_deliverable_id: data.fulfills_project_deliverable_id || undefined,
      }),
    onSuccess: () => {
      onUpdate()
      setShowDeliverableForm(false)
      setDeliverableForm({
        title: '',
        assignee_id: '',
        due_date: '',
        location_type: '',
        location_path: '',
        verification: '',
        fulfills_project_deliverable_id: '',
      })
    },
  })

  const updateDeliverable = useMutation({
    mutationFn: ({ deliverableId, data }: { deliverableId: string; data: Partial<Deliverable> }) =>
      api.patch(`/people/admin/deliverables/${deliverableId}`, data),
    onSuccess: () => onUpdate(),
  })

  const deleteDeliverable = useMutation({
    mutationFn: (deliverableId: string) => api.delete(`/people/admin/deliverables/${deliverableId}`),
    onSuccess: () => onUpdate(),
  })

  const statusColors: Record<string, string> = {
    not_started: 'bg-gray-800 text-gray-300',
    in_progress: 'bg-blue-900/50 text-blue-300',
    at_risk: 'bg-red-900/50 text-red-300',
    complete: 'bg-green-900/50 text-green-300',
  }

  const deliverableStatusColors: Record<string, string> = {
    not_started: 'bg-gray-700 text-gray-400',
    in_progress: 'bg-blue-900/30 text-blue-400',
    complete: 'bg-green-900/30 text-green-400',
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="font-medium">{goal.title}</div>
          {goal.description && (
            <div className="text-sm text-[var(--color-text-secondary)] mt-1">
              {goal.description}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${statusColors[goal.status]}`}>
            {goal.status.replace('_', ' ')}
          </span>
          <button onClick={onDelete} className="text-red-400 hover:text-red-300 text-sm">
            &times;
          </button>
        </div>
      </div>

      <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mb-3">
        {goal.owner && <span>Owner: {goal.owner.name}</span>}
        {goal.project && <span>Project: {goal.project.name}</span>}
        {goal.due_date && <span>Due: {goal.due_date}</span>}
      </div>

      {/* Deliverables */}
      <div className="border-t border-[var(--color-border)] pt-3 mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[var(--color-text-secondary)] uppercase tracking-wide">
            Deliverables
          </span>
          {!showDeliverableForm && (
            <button
              onClick={() => setShowDeliverableForm(true)}
              className="text-xs text-[var(--color-brand)] hover:underline"
            >
              + Add
            </button>
          )}
        </div>

        {showDeliverableForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              createDeliverable.mutate(deliverableForm)
            }}
            className="bg-[var(--color-surface)] p-3 rounded mb-3 space-y-2"
          >
            <input
              type="text"
              value={deliverableForm.title}
              onChange={(e) => setDeliverableForm({ ...deliverableForm, title: e.target.value })}
              placeholder="Deliverable title"
              className="input w-full text-sm"
              required
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                value={deliverableForm.assignee_id}
                onChange={(e) => setDeliverableForm({ ...deliverableForm, assignee_id: e.target.value })}
                className="input text-sm"
              >
                <option value="">Same assignee</option>
                {people?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <input
                type="date"
                value={deliverableForm.due_date}
                onChange={(e) => setDeliverableForm({ ...deliverableForm, due_date: e.target.value })}
                className="input text-sm"
                placeholder="Due date"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={deliverableForm.location_type}
                onChange={(e) => setDeliverableForm({ ...deliverableForm, location_type: e.target.value })}
                className="input text-sm"
              >
                <option value="">Location type</option>
                <option value="github">GitHub</option>
                <option value="storage">Storage</option>
                <option value="external">External URL</option>
              </select>
              <input
                type="text"
                value={deliverableForm.location_path}
                onChange={(e) => setDeliverableForm({ ...deliverableForm, location_path: e.target.value })}
                placeholder="Path or URL"
                className="input text-sm"
              />
            </div>
            <textarea
              value={deliverableForm.verification}
              onChange={(e) => setDeliverableForm({ ...deliverableForm, verification: e.target.value })}
              placeholder="How to verify completion..."
              className="input w-full text-sm"
              rows={2}
            />
            {projectDeliverables && projectDeliverables.length > 0 && (
              <select
                value={deliverableForm.fulfills_project_deliverable_id}
                onChange={(e) => setDeliverableForm({ ...deliverableForm, fulfills_project_deliverable_id: e.target.value })}
                className="input text-sm w-full"
              >
                <option value="">Fulfills contract deliverable... (optional)</option>
                {projectDeliverables.map((pd) => (
                  <option key={pd.id} value={pd.id}>{pd.title}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary text-xs py-1">Add</button>
              <button
                type="button"
                onClick={() => setShowDeliverableForm(false)}
                className="btn btn-secondary text-xs py-1"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {goal.deliverables && goal.deliverables.length > 0 ? (
          <div className="space-y-2">
            {goal.deliverables.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{d.title}</span>
                    {d.location_type && (
                      <span className="text-xs px-1 py-0.5 bg-[var(--color-bg)] rounded">
                        {d.location_type}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs text-[var(--color-text-secondary)] mt-0.5">
                    {d.assignee && <span>{d.assignee.name}</span>}
                    {d.due_date && <span>Due: {d.due_date}</span>}
                    {d.location_path && (
                      <code className="truncate max-w-[200px]">{d.location_path}</code>
                    )}
                  </div>
                  {/* Fulfills connection (admin view only) */}
                  {projectDeliverables && projectDeliverables.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-[var(--color-text-secondary)]">Fulfills:</span>
                      <select
                        value={d.fulfills_project_deliverable_id || ''}
                        onChange={(e) => updateDeliverable.mutate({
                          deliverableId: d.id,
                          data: { fulfills_project_deliverable_id: e.target.value || null }
                        })}
                        className="text-xs bg-transparent border border-[var(--color-border)] rounded px-1 py-0.5"
                      >
                        <option value="">None</option>
                        {projectDeliverables.map((pd) => (
                          <option key={pd.id} value={pd.id}>{pd.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={d.status}
                    onChange={(e) => updateDeliverable.mutate({
                      deliverableId: d.id,
                      data: { status: e.target.value as Deliverable['status'] }
                    })}
                    className={`text-xs px-1.5 py-0.5 rounded border-0 ${deliverableStatusColors[d.status]}`}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="complete">Complete</option>
                  </select>
                  <button
                    onClick={() => {
                      if (confirm(`Delete deliverable "${d.title}"?`)) {
                        deleteDeliverable.mutate(d.id)
                      }
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    &times;
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-text-secondary)]">No deliverables</p>
        )}
      </div>
    </div>
  )
}
